import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ApplicationIcon,
  Box,
  Chip,
  Icon,
  Link as DSLink,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import { type FormValues } from '@saviynt/formforge';
import cx from 'classnames';

import {
  CF_BLU_BLD,
  CF_NEU_BLD,
  CF_NEU_MED,
  CF_NEU_STL,
  CHAT_BLOCK,
  testIds,
} from '../../constants';
import { useLocalize } from '../../hooks';
import {
  type CombinedFormBlock,
  type CreateStage,
  type CustomBlock,
  type FormBlock,
  type FormForgeBlock,
  type StatusStage,
} from '../../types';
import { getLogoEndpointUrl } from '../../utils';
import { FORM_IP_MAP, type FormFieldState } from '../Form';
import {
  type AccessListResponse,
  AccessTypeMap,
  type AccountOwnerListResponse,
  type ApplicationListResponse,
  type ApplicationRoleAccessData,
  type EnterpriseRoleAccessData,
  type EntitlementAccessData,
  LIST_VIEWER_VARIANT,
  OWNERS_RANK_OPTIONS,
  OWNERS_TYPE,
  type ServiceAccountListResponse,
} from '../ListViewer';

import { REVIEW_CARD_TYPE } from './constants';
import { type CardVariantType } from './types';
import { getSpecialFormLabels, getSpecialFormLineLimits } from './utils';

import './ReviewCard.css';

interface ReviewCardListItemProps {
  label: string;
  children: React.ReactNode;
}

function ReviewCardListItem({ label, children }: ReviewCardListItemProps) {
  return (
    <Box tag='div' className='ReviewCardListItem'>
      <Box tag='div' className='ReviewCardListItem-label'>
        <Typography kind='body2' color={CF_NEU_BLD} lineLimit={1}>
          {label}
        </Typography>
      </Box>
      <Box tag='div' className='ReviewCardListItem-children'>
        {children}
      </Box>
    </Box>
  );
}

interface ReviewCardProps {
  type: CardVariantType;
  stages: (CreateStage | StatusStage)[];
}

/**
 * ReviewCard
 */
function ReviewCard({
  type,
  stages,
}: ReviewCardProps): React.ReactElement | null {
  const l = useLocalize();

  const renderForm = (fields?: FormFieldState[]) =>
    fields?.map((f) => {
      const isEditable = Boolean(f.isEditable === undefined || f.isEditable);
      const isValueBased = Boolean(
        f.type === FORM_IP_MAP.INPUT ||
          f.type === FORM_IP_MAP.TEXTAREA ||
          f.type === FORM_IP_MAP.DATE
      );
      const isOptionsBased = Boolean(
        f.type === FORM_IP_MAP.RADIO ||
          f.type === FORM_IP_MAP.SELECT ||
          f.type === FORM_IP_MAP.CHECKBOX
      );
      const isFileBased = Boolean(f.type === FORM_IP_MAP.UPLOAD);
      const key = `${f.type}-${f.name}`;

      if (isEditable && isValueBased) {
        return (
          <ReviewCardListItem
            key={key}
            label={l(getSpecialFormLabels(f.name, f.label))}>
            <Box tag='div' className='ReviewCardListItem-form'>
              <Typography
                kind='body3'
                lineLimit={getSpecialFormLineLimits(f.name)}>
                {f.value && String(f.value).trim().length > 0 ? (
                  f.value
                ) : (
                  <FormattedMessage
                    id='3vmxQe'
                    defaultMessage='None'
                    description='Text to indicate that there is no value for a form field in the ReviewCard component'
                  />
                )}
              </Typography>
            </Box>
          </ReviewCardListItem>
        );
      }

      if (isEditable && isOptionsBased) {
        return (
          <ReviewCardListItem
            key={key}
            label={l(getSpecialFormLabels(f.name, f.label))}>
            <Box tag='div' className='ReviewCardListItem-form'>
              <Typography kind='body3' lineLimit={1}>
                {f.options && f.options.length > 0 ? (
                  f.options.map((o) => o.label).join(', ')
                ) : (
                  <FormattedMessage
                    id='3vmxQe'
                    defaultMessage='None'
                    description='Text to indicate that there is no value for a form field in the ReviewCard component'
                  />
                )}
              </Typography>
            </Box>
          </ReviewCardListItem>
        );
      }

      if (isEditable && isFileBased) {
        return (
          <ReviewCardListItem
            key={key}
            label={l(getSpecialFormLabels(f.name, f.label))}>
            <Box tag='div' className='ReviewCardListItem-form'>
              <Box tag='div' className='ReviewCardListItem-formFiles'>
                {f.files && f.files.length > 0 ? (
                  <>
                    {f.files.slice(0, 3).map((o) => (
                      <Chip
                        key={o.fileSize}
                        kind='outlined'
                        label={
                          <Typography kind='body3' isNoWrap lineLimit={1}>
                            {o.fileName}
                          </Typography>
                        }
                        size='small'
                        leftIcon={<Icon kind='Document' />}
                        isInteractionDisabled
                      />
                    ))}
                    {f.files.length > 3 && (
                      <Tooltip
                        trigger={
                          <Typography kind='body3-bold' color={CF_NEU_BLD}>
                            <FormattedMessage
                              id='IUFCFe'
                              defaultMessage='+{moreCount} more'
                              description='Text to indicate that there are more items not currently displayed in the ReviewCard component'
                              values={{
                                moreCount: f.files.length - 3,
                              }}
                            />
                          </Typography>
                        }
                        text={f.files
                          .slice(3)
                          .map((a) => a.fileName)
                          .join(', ')}
                        placement='top'
                      />
                    )}
                  </>
                ) : (
                  <Typography kind='body3' color={CF_NEU_MED}>
                    <FormattedMessage
                      id='3vmxQe'
                      defaultMessage='None'
                      description='Text to indicate that there is no value for a form field in the ReviewCard component'
                    />
                  </Typography>
                )}
              </Box>
            </Box>
          </ReviewCardListItem>
        );
      }

      return null;
    });

  const renderFormForge = (formData?: FormValues) =>
    Object.entries(formData ?? {}).map(([key, value]) => {
      // Types supported: string | number | MenuInputOption | string[] | MenuInputOption[] | Date | boolean | null
      // MenuInputOption { label: string; value: string; isDisabled?: boolean; isChecked?: boolean; }

      // null / empty primitives — nothing to show
      if (value === null) {
        return null;
      }

      if (typeof value === 'boolean') {
        return (
          <ReviewCardListItem key={key} label={l(key)}>
            <Box tag='div' className='ReviewCardListItem-form'>
              <Typography kind='body3'>
                {value ? (
                  <FormattedMessage
                    id='zILZXp'
                    defaultMessage='True'
                    description='Text to indicate a true boolean value for a form field in the ReviewCard component'
                  />
                ) : (
                  <FormattedMessage
                    id='g3sQmo'
                    defaultMessage='False'
                    description='Text to indicate a false boolean value for a form field in the ReviewCard component'
                  />
                )}
              </Typography>
            </Box>
          </ReviewCardListItem>
        );
      }

      // Primitives: string and number
      if (typeof value === 'string' || typeof value === 'number') {
        const display = String(value).trim();

        if (display.length === 0) {
          return null;
        }

        return (
          <ReviewCardListItem key={key} label={l(key)}>
            <Box tag='div' className='ReviewCardListItem-form'>
              <Typography kind='body3' lineLimit={1}>
                {display}
              </Typography>
            </Box>
          </ReviewCardListItem>
        );
      }

      // Arrays: string[] | MenuInputOption[]
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return null;
        }

        const confinedList = value.reduce<string[]>((arr, item) => {
          if (typeof item === 'string') {
            return [...arr, item];
          }

          return [...arr, item.label];
        }, []);

        return (
          <ReviewCardListItem key={key} label={l(key)}>
            <Box tag='div' className='ReviewCardListItem-form'>
              <Typography kind='body3' lineLimit={1}>
                {confinedList.join(', ')}
              </Typography>
            </Box>
          </ReviewCardListItem>
        );
      }

      // Objects: MenuInputOption or Date
      if (value instanceof Date) {
        return null;
      }

      if ('label' in value) {
        const typedValue = value;

        return (
          <ReviewCardListItem key={key} label={l(key)}>
            <Box tag='div' className='ReviewCardListItem-form'>
              <Typography kind='body3' lineLimit={1}>
                {typedValue.label}
              </Typography>
            </Box>
          </ReviewCardListItem>
        );
      }

      return null;
    });

  const getCreateLisItem = (stage: CreateStage): React.ReactNode => {
    if (stage.type === CHAT_BLOCK.CUSTOM) {
      const typedStage = stage as CustomBlock;

      if (typedStage.variant === LIST_VIEWER_VARIANT.APPLICATIONS) {
        const typedSelections =
          typedStage.selections as ApplicationListResponse[];

        return (
          <ReviewCardListItem
            key='review-card-application-list'
            label='Application'>
            <Box tag='div' className='ReviewCardListItem-application'>
              {typedSelections.map((a) => (
                <Chip
                  key={a.applicationId}
                  kind='outlined'
                  label={
                    <Typography kind='body3' isNoWrap lineLimit={1}>
                      {a.applicationDisplayName ?? a.applicationName}
                    </Typography>
                  }
                  size='small'
                  leftIcon={
                    <ApplicationIcon
                      size='xxSmall'
                      src={getLogoEndpointUrl(a.applicationLogo)}
                    />
                  }
                  isInteractionDisabled
                />
              ))}
            </Box>
          </ReviewCardListItem>
        );
      }

      if (typedStage.variant === LIST_VIEWER_VARIANT.ACCOUNT_OWNERS) {
        const typedSelections =
          typedStage.selections as AccountOwnerListResponse[];

        return (
          <ReviewCardListItem key='review-card-owners-list' label='Owner'>
            <Box tag='div' className='ReviewCardListItem-owners'>
              {typedSelections.map((o) => {
                const rank =
                  OWNERS_RANK_OPTIONS.find(
                    (r) => String(r.value) === String(o.rank ?? '')
                  )?.label ?? '';

                return (
                  <Box
                    key={o.type === OWNERS_TYPE.USER ? o.id : o.usergroupkey}
                    tag='div'
                    className='ReviewCardListItem-ownersListItem'>
                    <Typography kind='body3' lineLimit={1}>
                      <FormattedMessage
                        id='kzR59h'
                        defaultMessage='Rank {rank}:'
                        description='Rank holder'
                        values={{ rank }}
                      />
                      &nbsp;
                    </Typography>
                    <Chip
                      kind='outlined'
                      label={
                        <Typography kind='body3' isNoWrap lineLimit={1}>
                          {o.type === OWNERS_TYPE.USER
                            ? o.displayname ?? (
                                <FormattedMessage
                                  id='P1RA7H'
                                  defaultMessage='{firstname} {lastname}'
                                  description='Label for owner name in the ReviewCard component'
                                  values={{
                                    firstname: o.firstname,
                                    lastname: o.lastname,
                                  }}
                                />
                              )
                            : o.user_groupname}
                        </Typography>
                      }
                      size='small'
                      isInteractionDisabled
                    />
                  </Box>
                );
              })}
            </Box>
          </ReviewCardListItem>
        );
      }

      if (typedStage.variant === LIST_VIEWER_VARIANT.ACCESS) {
        const typedSelections = typedStage.selections as AccessListResponse[];
        const accessItems = Array.isArray(typedSelections)
          ? typedSelections.flat()
          : [];

        return (
          <ReviewCardListItem key='review-card-access-list' label='Access'>
            <Box tag='div' className='ReviewCardListItem-access'>
              {accessItems.slice(0, 3).map((a) => {
                if (a.type === AccessTypeMap.entitlement) {
                  const typedA = a as EntitlementAccessData[number];

                  return (
                    <Chip
                      key={typedA.entitlement_valuekey}
                      kind='outlined'
                      label={
                        <Typography kind='body3' isNoWrap lineLimit={1}>
                          {typedA.displayname &&
                          typedA.displayname.trim().length > 0
                            ? typedA.displayname
                            : typedA.entitlement_value}
                        </Typography>
                      }
                      size='small'
                      leftIcon={<Icon kind='Entitlement' />}
                      isInteractionDisabled
                    />
                  );
                }

                if (a.type === AccessTypeMap.enterpriseRole) {
                  const typedA = a as EnterpriseRoleAccessData[number];

                  return (
                    <Chip
                      key={typedA.roleid}
                      kind='outlined'
                      label={
                        <Typography kind='body3' isNoWrap lineLimit={1}>
                          {typedA.displayname &&
                          typedA.displayname.trim().length > 0
                            ? typedA.displayname
                            : typedA.rolename}
                        </Typography>
                      }
                      size='small'
                      leftIcon={<Icon kind='EnterpriseRole' />}
                      isInteractionDisabled
                    />
                  );
                }

                if (a.type === AccessTypeMap.applicationRole) {
                  const typedA = a as ApplicationRoleAccessData[number];

                  return (
                    <Chip
                      key={typedA.roleid}
                      kind='outlined'
                      label={
                        <Typography kind='body3' isNoWrap lineLimit={1}>
                          {typedA.displayname &&
                          typedA.displayname.trim().length > 0
                            ? typedA.displayname
                            : typedA.rolename}
                        </Typography>
                      }
                      size='small'
                      leftIcon={<Icon kind='ApplicationRole' />}
                      isInteractionDisabled
                    />
                  );
                }

                return null;
              })}
              {accessItems.length > 3 && (
                <Tooltip
                  trigger={
                    <Typography kind='body3-bold' color={CF_NEU_BLD}>
                      <FormattedMessage
                        id='IUFCFe'
                        defaultMessage='+{moreCount} more'
                        description='Text to indicate that there are more items not currently displayed in the ReviewCard component'
                        values={{
                          moreCount: accessItems.length - 3,
                        }}
                      />
                    </Typography>
                  }
                  text={(() => {
                    const displayNames: string[] = [];

                    accessItems.slice(3).forEach((a) => {
                      if (a.type === AccessTypeMap.entitlement) {
                        const typedA = a as EntitlementAccessData[number];

                        displayNames.push(
                          typedA.displayname &&
                            typedA.displayname.trim().length > 0
                            ? typedA.displayname
                            : typedA.entitlement_value
                        );
                      }

                      if (a.type === AccessTypeMap.enterpriseRole) {
                        const typedA = a as EnterpriseRoleAccessData[number];

                        displayNames.push(
                          typedA.displayname &&
                            typedA.displayname.trim().length > 0
                            ? typedA.displayname
                            : typedA.rolename
                        );
                      }

                      if (a.type === AccessTypeMap.applicationRole) {
                        const typedA = a as ApplicationRoleAccessData[number];

                        displayNames.push(
                          typedA.displayname &&
                            typedA.displayname.trim().length > 0
                            ? typedA.displayname
                            : typedA.rolename
                        );
                      }
                    });

                    return displayNames.join(', ');
                  })()}
                  placement='top'
                />
              )}
            </Box>
          </ReviewCardListItem>
        );
      }

      if (typedStage.variant === LIST_VIEWER_VARIANT.SERVICE_ACCOUNTS) {
        const typedSelections =
          typedStage.selections as ServiceAccountListResponse[];

        return (
          <ReviewCardListItem
            key='review-card-application-list'
            label='Service Account'>
            <Box tag='div' className='ReviewCardListItem-application'>
              {typedSelections.map((a) => (
                <Typography kind='body3' isNoWrap lineLimit={1}>
                  {a.name}
                </Typography>
              ))}
            </Box>
          </ReviewCardListItem>
        );
      }

      return null;
    }

    if (stage.type === CHAT_BLOCK.FORM) {
      const typedStage = stage as FormBlock;
      const fields = typedStage.elements as unknown as FormFieldState[];

      return renderForm(fields);
    }

    if (stage.type === CHAT_BLOCK.FORMFORGE) {
      const typedStage = stage as FormForgeBlock;

      return renderFormForge(typedStage.formData);
    }

    if (stage.type === CHAT_BLOCK.COMBINEDFORM) {
      const typedStage = stage as CombinedFormBlock;
      const customFields = typedStage.custom?.formJson;
      const externalFields = typedStage.external?.formData;

      return (
        <>
          {renderForm(customFields)}
          {renderFormForge(externalFields)}
        </>
      );
    }

    return null;
  };

  const getStatusCards = (stage: StatusStage): React.ReactNode => (
    <>
      <Typography kind='h3' color={CF_NEU_BLD}>
        {stage.type === REVIEW_CARD_TYPE.CREATE && (
          <FormattedMessage
            id='ae43CJ'
            defaultMessage='Request - Create Service Account'
            description='Heading for create service account request in the ReviewCard component'
          />
        )}
      </Typography>
      <Box tag='div' className='ReviewCard-statusContent'>
        <Box tag='div' className='ReviewCard-statusContentListItem'>
          <ApplicationIcon
            size='xxSmall'
            src={getLogoEndpointUrl(stage.accountLogo)}
          />
          <Typography kind='body2' color={CF_NEU_BLD} lineLimit={1}>
            {stage.accountName}
          </Typography>
        </Box>
        <Typography kind='body2' color={CF_NEU_STL} lineLimit={1}>
          <FormattedMessage
            id='Oywj42'
            defaultMessage='Request ID:'
            description='Label for request ID in the ReviewCard component'
          />
          &nbsp;
          <span className='Bold-color-text'>{stage.requestId}</span>
        </Typography>
        <Box tag='div' className='ReviewCard-statusContentListItem'>
          <Typography kind='body2' color={CF_NEU_STL} lineLimit={1}>
            <FormattedMessage
              id='pGcrxs'
              defaultMessage='Request Status:'
              description='Label for request status in the ReviewCard component'
            />
            &nbsp;
          </Typography>
          <Chip
            kind='subtleFilled'
            label={(() => {
              if (stage.requestStatus === 'IN-PROGRESS') {
                return 'In Progress';
              }

              return '-';
            })()}
            color={(() => {
              if (stage.requestStatus === 'IN-PROGRESS') {
                return 'info';
              }

              return 'neutral';
            })()}
            size='xSmall'
            isInteractionDisabled
          />
        </Box>
        <Typography kind='body2' color={CF_NEU_STL} lineLimit={1}>
          <FormattedMessage
            id='/LUtAO'
            defaultMessage='Review Method:'
            description='Label for review method in the ReviewCard component'
          />
          &nbsp;
          <span className='Bold-color-text'>{stage.reviewMethod}</span>
        </Typography>
        <Typography kind='body2' color={CF_NEU_STL} lineLimit={1}>
          <FormattedMessage
            id='RnU5O7'
            defaultMessage='Reviewer:'
            description='Label for reviewer in the ReviewCard component'
          />
          &nbsp;
          <span className='Bold-color-text'>{stage.reviewer}</span>
        </Typography>
        <Typography kind='body2' color={CF_NEU_STL} lineLimit={1}>
          <FormattedMessage
            id='SkleI4'
            defaultMessage='Estimated Time Until Access Granted:'
            description='Label for estimated time until access is granted in the ReviewCard component'
          />
          &nbsp;
          <span className='Bold-color-text'>
            {(() => {
              const days = Math.floor(stage.estimatedTime / 86400);

              const remainingAfterDays = stage.estimatedTime % 86400;
              const hours = Math.floor(remainingAfterDays / 3600);

              const remainingAfterHours = remainingAfterDays % 3600;
              const minutes = Math.floor(remainingAfterHours / 60);

              const seconds = remainingAfterHours % 60;

              // Build formatted string
              const parts = [];

              if (days) {
                parts.push(`${days}d`);
              }

              if (hours) {
                parts.push(`${hours}h`);
              }

              if (minutes) {
                parts.push(`${minutes}m`);
              }

              if (seconds) {
                parts.push(`${seconds}s`);
              }

              return parts.join(' ');
            })()}
          </span>
        </Typography>
      </Box>
      <DSLink
        url='/'
        text='View Request History'
        size='large'
        suffixIconKind='JumpLink'
        className='ReviewCard-statusContentLink'
      />
    </>
  );

  return (
    <Box
      tag='div'
      className={cx(
        'ReviewCard',
        type === REVIEW_CARD_TYPE.CREATE && 'ReviewCard--isCreate',
        type === REVIEW_CARD_TYPE.STATUS && 'ReviewCard--isStatus'
      )}
      dataTestId={testIds.GEN_REVIEW_CARD_ID}>
      {type === REVIEW_CARD_TYPE.CREATE && (
        <Box
          tag='div'
          className={cx('ReviewCard-wrapper', 'ReviewCard-wrapper--isCreate')}>
          {stages.map((stage, idx) => {
            const typedStage = stage as CreateStage;
            const key = `create-stage-${idx}`;

            return (
              <React.Fragment key={key}>
                {getCreateLisItem(typedStage)}
              </React.Fragment>
            );
          })}
        </Box>
      )}
      {type === REVIEW_CARD_TYPE.STATUS &&
        stages.map((stage) => {
          const typedStage = stage as StatusStage;

          return (
            <Box
              key={stage.type}
              tag='div'
              className={cx(
                'ReviewCard-wrapper',
                'ReviewCard-wrapper--isStatus'
              )}>
              <Icon kind='ServiceAccount' size='medium' color={CF_BLU_BLD} />
              <Box tag='div' className='ReviewCard-statusWrapper'>
                {getStatusCards(typedStage)}
              </Box>
            </Box>
          );
        })}
    </Box>
  );
}

export default memo(ReviewCard);
